/**
 * bankMatching/types — Ergebnis- und Kandidaten-Typen der Matching-Engine
 * (Bank-Abgleich BR-4 Eingang + BR-5 Ausgang).
 *
 * Referenz: architecture-bank-reconciliation.md §8.2 (Kandidaten-Shapes),
 * §8.3/§8.6 (Score-Tabellen), §8.4 (Score-Floor/Auto/Eindeutigkeit),
 * §8.9 (Rücklastschrift), §8.10 (kanonische Matrix M1–M11 — führend).
 *
 * Geld-Disziplin: ALLE Beträge sind Integer-Cents (EUR). `MatchableInvoice`/
 * `MatchableExpense` sind reine Engine-Shapes ohne Firestore-Kopplung; die
 * Konvertierung `invoices.totalAmount`(Float) → Cents passiert AUSSCHLIESSLICH
 * im Mapper (`toMatchableInvoices`, §15). Datumsfelder sind ISO `YYYY-MM-DD`
 * (lexikografisch == chronologisch), niemals `Date`-Objekte über Modulgrenzen.
 *
 * @module bankMatching/types
 */

// ─────────────────────────────────────────────────────────────────────────
// Kandidaten-Shapes (Mapper liefert, Engine konsumiert) — §8.2
// ─────────────────────────────────────────────────────────────────────────

/**
 * Rechnungs-Kandidat für das Eingangs-Matching (aus `invoices` + `payments`
 * + `dunning_events`, Mapper §9.2/§11).
 */
export interface MatchableInvoice {
  invoiceId: string;
  /** z.B. 'Re2026-042'. */
  invoiceNumber: string;
  buyerName: string;
  /** ISO `YYYY-MM-DD` (BT-2). */
  invoiceDateISO: string;
  /** ISO `YYYY-MM-DD` (BT-9) oder `null`. */
  dueDateISO: string | null;
  /** `eurosToCents(invoice.totalAmount)` — Integer-Cents. */
  storedGrandTotalCents: number;
  /** Aus `computeOpenItemStatus().openAmountCents` VOR diesem Match. */
  openAmountCents: number;
  /** `invoice.rawData.skonto.percentage` (§15-Lesart) oder `null`. */
  skontoPercentage: number | null;
  /** `invoice.rawData.skonto.days` oder `null`. */
  skontoDays: number | null;
  /**
   * v2 (CFO Risiko-6): ≥1 `dunning_events`-Doc zur invoiceId UND Rechnung
   * noch offen. Aktiviert den Dunning-Guard (§8.4 Regel 5).
   */
  hasActiveDunning: boolean;
  /** Höchste aktive Mahnstufe (UI: „stoppt Mahnstufe n") oder `null`. */
  dunningLevel: number | null;
}

/**
 * Ausgaben-Kandidat für das Ausgangs-Matching (aus `expenses` +
 * `received_invoices`-Join, Mapper §9.2/§11).
 */
export interface MatchableExpense {
  expenseId: string;
  supplierName: string;
  invoiceNumber: string | null;
  /** Signiert wie gespeichert (381-Gutschrift negativ) — Integer-Cents. */
  grossCents: number;
  /** ISO `YYYY-MM-DD`. */
  expenseDateISO: string;
  /** ISO `YYYY-MM-DD` oder `null`. */
  dueDateISO: string | null;
  /** `received_invoices.data.paymentMeans[..].iban` (erste befüllte, normalisiert). */
  supplierIban: string | null;
  /** Aus tx-seitigem Join — abgeglichene Expenses sind kein Kandidat. */
  alreadyMatched: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Reason-Codes & Match-Ergebnis — §8.2
// ─────────────────────────────────────────────────────────────────────────

/**
 * Erklärbarkeits-Code eines einzelnen Score-Signals. Enthält die
 * Betrags-/Namens-/Datums-Signale beider Richtungen (§8.3/§8.6) sowie die
 * v2-Guards `DUNNING_GUARD` (Δ0) und `REVERSAL_OF_CREDIT` (§8.9).
 */
export type MatchReasonCode =
  | 'NUMBER_EXACT'
  | 'AMOUNT_OPEN_EXACT'
  | 'AMOUNT_TOTAL_EXACT'
  | 'AMOUNT_SKONTO'
  | 'AMOUNT_PARTIAL'
  | 'NAME_STRONG'
  | 'NAME_WEAK'
  | 'DATE_PLAUSIBLE'
  | 'DATE_BEFORE_DOC'
  | 'IBAN_EXACT'
  | 'AMOUNT_EXACT'
  | 'DUNNING_GUARD'
  | 'REVERSAL_OF_CREDIT';

/**
 * Enum-artiger Konstanten-Zugriff auf die Reason-Codes (vermeidet String-
 * Literale an Call-Sites — Refactor-fest).
 */
export const MATCH_REASON = {
  NUMBER_EXACT: 'NUMBER_EXACT',
  AMOUNT_OPEN_EXACT: 'AMOUNT_OPEN_EXACT',
  AMOUNT_TOTAL_EXACT: 'AMOUNT_TOTAL_EXACT',
  AMOUNT_SKONTO: 'AMOUNT_SKONTO',
  AMOUNT_PARTIAL: 'AMOUNT_PARTIAL',
  NAME_STRONG: 'NAME_STRONG',
  NAME_WEAK: 'NAME_WEAK',
  DATE_PLAUSIBLE: 'DATE_PLAUSIBLE',
  DATE_BEFORE_DOC: 'DATE_BEFORE_DOC',
  IBAN_EXACT: 'IBAN_EXACT',
  AMOUNT_EXACT: 'AMOUNT_EXACT',
  DUNNING_GUARD: 'DUNNING_GUARD',
  REVERSAL_OF_CREDIT: 'REVERSAL_OF_CREDIT',
} as const;

/** Ein einzelnes, erklärbares Score-Signal eines (tx, Kandidat)-Paars. */
export interface MatchReason {
  code: MatchReasonCode;
  /** Menschenlesbar für den UI-Chip, z.B. 'Re2026-042 im Verwendungszweck'. */
  detail: string;
  /** Score-Beitrag (kann 0 sein, z.B. `DUNNING_GUARD`, oder negativ). */
  scoreDelta: number;
}

/** Art des Match-Vorschlags. */
export type MatchKind =
  | 'invoice'
  | 'expense'
  | 'split-invoices'
  | 'internal-transfer'
  | 'payment-reversal';

/** Confidence-Stufe (`auto` nur bei erfüllten §8.4-Bedingungen). */
export type MatchConfidence = 'auto' | 'suggest';

/**
 * Ein Match-Vorschlag der Engine für GENAU eine Transaktion. Mehrere
 * Kandidaten derselben tx erscheinen als mehrere `MatchSuggestion`-Objekte
 * mit derselben `txId` und je `targetIds.length === 1` (§8.3 Soll-Paar 5) —
 * mehrere `targetIds` gibt es nur bei `split-invoices`.
 */
export interface MatchSuggestion {
  txId: string;
  kind: MatchKind;
  /** invoiceIds bzw. expenseIds (split: mehrere). */
  targetIds: string[];
  score: number;
  confidence: MatchConfidence;
  reasons: MatchReason[];
  skontoDetected: boolean;
  /** Nur `kind:'split-invoices'` — Σ MUSS exakt `tx.amountCents` ergeben (Invariante). */
  splitParts?: Array<{ invoiceId: string; amountCents: number }>;
  /** `tx > open` bei klarem Nummern-Bezug — UI zeigt Überzahlung. */
  overpaymentCents?: number;
  /** Nur `kind:'payment-reversal'` — storno-relevante Original-Payment-IDs (§8.9). */
  reversalOfPaymentIds?: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// Vorstufen-Shapes (§8.6 Vorstufe 1 / §8.9) — pure
// ─────────────────────────────────────────────────────────────────────────

/**
 * Ergebnis von `detectInternalTransfers` — ein Umbuchungs-Paar.
 * `creditTxId === null`: Gegenseite (noch) nicht importiert, nur die
 * IBAN-evidente Debit-Seite ist klassifiziert (§8.8).
 */
export interface InternalTransferPair {
  debitTxId: string;
  creditTxId: string | null;
}

/**
 * Ergebnis von `detectPaymentReversals` — eine erkannte Rücklastschrift
 * (Rücknahme einer bereits verbuchten Gutschrift, §8.9).
 */
export interface ReversalSuggestion {
  /** Die Rücklastschrift (amountCents < 0, unmatched). */
  debitTxId: string;
  /** Die frühere, gematchte Gutschrift. */
  originalTxId: string;
  /** Deren `paymentIds` (≥1) — Storno-Kopplung im Confirm-Batch. */
  reversalOfPaymentIds: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// Mapper-Eingangs-Shape (Firestore invoice → Engine)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Minimal-Shape eines Firestore-`invoices`-Dokuments, wie ihn
 * `toMatchableInvoices` liest. Spiegelt EXAKT die Lesart der OpenItemsPage
 * (Source-of-Truth für Skonto-/DueDate-/InvoiceDate-Extraktion, §15) — daher
 * bewusst nur die abgeglichenen Felder statt des vollen `invoiceTypes`-Docs
 * (das die Engine nicht braucht und dessen Kopplung sie pure-fremd machte).
 *
 * `invoiceDate` ist `string | unknown`, weil die OpenItemsPage einen
 * Firestore-`Timestamp` ODER String toleriert; der Mapper normalisiert beides
 * über einen `toDateISO`-Helper (kein Firestore-Import in dieser reinen Datei).
 */
export interface InvoiceDoc {
  id: string;
  invoiceNumber: string;
  /** Firestore `Timestamp` (Objekt mit `.toDate()`) ODER ISO-String. */
  invoiceDate: unknown;
  buyerName: string;
  totalAmount: number;
  currency?: string;
  /** Top-Level dueDate (M1b) — Vorrang vor rawData. */
  dueDate?: string | null;
  isCancelled?: boolean;
  isCancellation?: boolean;
  rawData?: {
    invoiceDate?: string;
    dueDate?: string;
    paymentTerms?: { dueDate?: string };
    /** §15-Lesart: `.percentage`/`.days` (NICHT `.percent`). */
    skonto?: { percentage?: number; days?: number } | null;
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Schwellwert-Konstanten — §8.4
// ─────────────────────────────────────────────────────────────────────────

/** Score-Floor: Paare mit Score < 30 erscheinen NICHT im Ergebnis (§8.4, Tester K2). */
export const MIN_SUGGESTION_SCORE = 30;

/** Auto-Schwelle: Mindest-Score für `confidence:'auto'` (§8.4 Bedingung 1). */
export const AUTO_MIN_SCORE = 80;

/** Auto-Eindeutigkeit: Best − Zweitbester muss ≥ diesem Wert sein (§8.4 Bedingung 2). */
export const AUTO_UNIQUENESS_GAP = 25;
